"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import Image from "next/image";
import { useLenis } from "lenis/react";
import {
  ImageIcon,
  Lightbulb,
  Settings,
  Users,
  MessageCircle,
  Share2,
  Rocket,
  ChartLine,
  type LucideIcon,
} from "lucide-react";
import { Reveal, RevealHeader } from "@/components/ui/Reveal";

const processIconMap = {
  Lightbulb,
  Settings,
  Users,
  MessageCircle,
  Share2,
  Rocket,
  ChartLine,
} as const satisfies Record<string, LucideIcon>;

export type ProcessIconName = keyof typeof processIconMap;

export type ProcessStep = {
  id: string;
  label: string;
  iconName: ProcessIconName;
  title: string;
  description: string;
  imageSrc?: string;
  imageAlt?: string;
};

const PROCESS_IMAGE_BASE = "/landing/process%20section";
const LG_BREAKPOINT = 1024;

const defaultSteps: ProcessStep[] = [
  {
    id: "manuscript-consultation",
    label: "Manuscript Consultation",
    iconName: "Lightbulb",
    title: "Manuscript Consultation",
    description:
      "Working with experienced book publishers helps shape your manuscript into a clear and structured draft. This stage focuses on identifying strengths, refining ideas, and addressing gaps. With expert guidance and self publishing services, authors gain clarity about their direction. Feedback is practical and focused, helping you understand what works and what needs improvement. This step builds a strong foundation, ensuring your manuscript is ready for the next stages of development and publishing success.",
    imageSrc: `${PROCESS_IMAGE_BASE}/our-story.png`,
    imageAlt: "Books and typewriter for manuscript consultation",
  },
  {
    id: "editing-refinement",
    label: "Editing Refinement",
    iconName: "Settings",
    title: "Editing Refinement",
    description:
      "Editing ensures your manuscript communicates ideas clearly and professionally. During this stage, your content is refined for structure, tone, and readability. Authors preparing to publish my book on Amazon benefit from detailed editing that aligns with platform standards. Every sentence is reviewed for clarity and consistency. The goal is to strengthen your message while maintaining your voice. This step ensures your manuscript meets expectations and provides readers with a polished and engaging experience.",
    imageSrc: `${PROCESS_IMAGE_BASE}/web-bann.webp`,
    imageAlt: "Digital and print book editing examples",
  },
  {
    id: "design-formatting",
    label: "Design Formatting",
    iconName: "Users",
    title: "Design Formatting",
    description:
      "Design and formatting shape how your book is experienced by readers. Professional book publishing services ensure your manuscript is visually clean and easy to read. Layout, typography, and spacing are carefully structured for both print and digital formats. A well-formatted book improves readability and creates a strong impression. This stage focuses on presenting your content in a professional manner, ensuring your book meets industry standards and delivers a smooth reading experience.",
    imageSrc: `${PROCESS_IMAGE_BASE}/book-cover-design.png`,
    imageAlt: "Book cover design and formatting mockups",
  },
  {
    id: "isbn-metadata",
    label: "ISBN Metadata",
    iconName: "MessageCircle",
    title: "ISBN Metadata",
    description:
      "Proper registration ensures your book is recognized and accessible across platforms. Understanding systems used by traditional publishing companies helps position your book correctly. With support from Amazon book publishing services, metadata such as title, author details, and categories are carefully managed. Accurate information improves discoverability and ensures your book appears in relevant searches. This stage focuses on technical details that play a major role in how your book is listed and found by readers.",
    imageSrc: `${PROCESS_IMAGE_BASE}/ghost-b.webp`,
    imageAlt: "ISBN and metadata publishing visual",
  },
  {
    id: "publishing-distribution",
    label: "Publishing Distribution",
    iconName: "Share2",
    title: "Publishing Distribution",
    description:
      "Distribution determines how far your book can reach. With reliable self publishing services, your book becomes available across multiple platforms and regions. This stage ensures your work is accessible to readers worldwide. Distribution channels are selected based on your goals and audience. The process focuses on visibility and accessibility, helping your book reach the right readers. Proper distribution ensures your efforts translate into real opportunities for discovery and engagement.",
    imageSrc: `${PROCESS_IMAGE_BASE}/about-bann.webp`,
    imageAlt: "Published book covers for distribution",
  },
  {
    id: "post-publication-support",
    label: "Post-Publication Support",
    iconName: "MessageCircle",
    title: "Post-Publication Support",
    description:
      "Support does not end after your book is published. Guidance from book publishers for new authors helps you navigate the next steps with confidence. This includes updates, revisions, and ongoing assistance as needed. Authors often require help understanding performance and planning future steps. Continued support ensures your book remains relevant and accessible. This stage focuses on maintaining quality while helping you grow as an author and build long-term success.",
    imageSrc: `${PROCESS_IMAGE_BASE}/make-book.webp`,
    imageAlt: "Post-publication book support visual",
  },
];

function StickyProcessImage({
  steps,
  activeIndex,
}: {
  steps: ProcessStep[];
  activeIndex: number;
}) {
  const [failed, setFailed] = useState<Record<string, boolean>>({});
  const [bounceToken, setBounceToken] = useState(0);
  const prevIndexRef = useRef(activeIndex);
  const activeStep = steps[activeIndex];
  const activeFailed = activeStep?.id ? Boolean(failed[activeStep.id]) : false;
  const showFallback = !activeStep?.imageSrc || activeFailed;

  useEffect(() => {
    if (prevIndexRef.current !== activeIndex) {
      setBounceToken((token) => token + 1);
      prevIndexRef.current = activeIndex;
    }
  }, [activeIndex]);

  // Mount nearby images so outgoing and incoming frames can crossfade smoothly.
  const shouldRender = (index: number) => Math.abs(index - activeIndex) <= 2;

  return (
    <div className="relative h-full w-full overflow-hidden">
      {steps.map((step, index) => {
        if (!step.imageSrc || failed[step.id] || !shouldRender(index)) return null;

        const isActive = index === activeIndex;

        return (
          <Image
            key={step.id}
            src={step.imageSrc}
            alt={step.imageAlt ?? step.title}
            fill
            sizes="(max-width: 1024px) 90vw, 40vw"
            quality={70}
            loading="lazy"
            data-bounce={isActive ? bounceToken : undefined}
            className={[
              "process-sticky-image absolute inset-0 m-auto object-contain p-2 sm:p-3 lg:p-0",
              isActive ? "is-active z-10" : "is-inactive pointer-events-none z-0",
            ].join(" ")}
            onError={() =>
              setFailed((prev) => ({ ...prev, [step.id]: true }))
            }
          />
        );
      })}

      {showFallback && (
        <div className="flex h-full w-full flex-col items-center justify-center rounded-xl bg-[#f5f0e8] text-gray-500">
          <ImageIcon className="h-12 w-12" aria-hidden="true" />
          <span className="mt-3 px-4 text-center text-sm">
            {activeStep?.imageAlt ?? "Process visual"}
          </span>
        </div>
      )}
    </div>
  );
}

function StepCopy({
  step,
  index,
  total,
  isActive,
}: {
  step: ProcessStep;
  index: number;
  total: number;
  isActive: boolean;
}) {
  return (
    <div
      className={[
        "process-content w-full max-w-none text-center transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] lg:max-w-xl lg:text-left",
        isActive ? "translate-y-0 opacity-100" : "opacity-0 lg:translate-y-4 lg:opacity-35",
      ].join(" ")}
    >
      <p className="font-heading text-xs font-semibold uppercase tracking-[0.18em] text-[#29361a]">
        Step {index + 1} of {total}
      </p>
      <h3 className="mt-3 font-heading text-2xl font-bold text-[#29361a] sm:text-3xl xl:text-4xl">
        {step.title}
      </h3>
      <p className="mt-4 text-base leading-relaxed text-gray-700 sm:mt-5 xl:text-lg">
        {step.description}
      </p>
    </div>
  );
}

export type ProcessSectionProps = {
  eyebrow?: string;
  heading?: string;
  description?: string;
  steps?: ProcessStep[];
  className?: string;
};

export function ProcessSection({
  eyebrow = "Process Section",
  heading = "A Clear, Guided Process To Help You Publish Your Book",
  description = "Publishing doesn't have to feel overwhelming. We simplify the entire journey into clear, manageable steps—so you always know where you are and what comes next.",
  steps = defaultSteps,
  className,
}: ProcessSectionProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const lenis = useLenis();

  const mobilePanelRefs = useRef<(HTMLElement | null)[]>([]);
  const desktopPanelRefs = useRef<(HTMLElement | null)[]>([]);
  const lockRef = useRef(false);
  const rafRef = useRef<number | null>(null);

  const updateFromScroll = useCallback(() => {
    if (lockRef.current) return;

    const panels =
      window.innerWidth >= LG_BREAKPOINT
        ? desktopPanelRefs.current
        : mobilePanelRefs.current;

    const viewportCenter = window.innerHeight * 0.5;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    panels.forEach((panel, index) => {
      if (!panel) return;
      const rect = panel.getBoundingClientRect();
      const panelCenter = rect.top + rect.height * 0.5;
      const distance = Math.abs(panelCenter - viewportCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    setActiveIndex((prev) => (prev === closestIndex ? prev : closestIndex));
  }, []);

  useEffect(() => {
    const onScrollOrResize = () => {
      if (rafRef.current !== null) return;
      rafRef.current = window.requestAnimationFrame(() => {
        rafRef.current = null;
        updateFromScroll();
      });
    };

    updateFromScroll();
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);

    return () => {
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
      }
    };
  }, [updateFromScroll]);

  const scrollToStep = useCallback(
    (index: number) => {
      lockRef.current = true;
      setActiveIndex(index);

      const panels =
        window.innerWidth >= LG_BREAKPOINT
          ? desktopPanelRefs.current
          : mobilePanelRefs.current;
      const panel = panels[index];

      if (panel && lenis) {
        lenis.scrollTo(panel, { offset: -100, duration: 1.2 });
      } else {
        panel?.scrollIntoView({ behavior: "smooth", block: "center" });
      }

      window.setTimeout(() => {
        lockRef.current = false;
        updateFromScroll();
      }, 750);
    },
    [updateFromScroll, lenis],
  );

  const onTabKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    if (event.key === "ArrowDown" || event.key === "ArrowRight") {
      event.preventDefault();
      scrollToStep(Math.min(index + 1, steps.length - 1));
    }
    if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
      event.preventDefault();
      scrollToStep(Math.max(index - 1, 0));
    }
  };

  return (
    <section
      className={["bg-white pb-16 sm:pb-20 lg:pb-24", className]
        .filter(Boolean)
        .join(" ")}
      aria-label="Publishing process"
    >
      <div className="mx-auto max-w-7xl px-4 pt-16 sm:px-6 lg:px-8 lg:pt-20">
        <RevealHeader
          eyebrow={
            <p className="font-heading text-xl font-semibold tracking-wide text-[#29361a] sm:text-[28px] sm:leading-9">
              {eyebrow}
            </p>
          }
          heading={
            <h2 className="mt-2 font-heading text-3xl font-bold text-[#b46c29] sm:text-4xl lg:text-[45px] lg:leading-[54px]">
              {heading}
            </h2>
          }
          description={
            <p className="mt-4 text-base leading-relaxed text-gray-700">
              {description}
            </p>
          }
        />
      </div>

      {/* Mobile: sticky image + vertical text panels (same behavior as desktop) */}
      <div className="relative mt-8 lg:hidden">
        <div className="sticky top-0 z-10 bg-white px-3 pb-4 pt-36 sm:px-5 sm:pt-40">
          <div className="mx-auto h-[54vw] max-h-85 min-h-55 w-full max-w-lg sm:h-[46vw] sm:max-h-95">
            <StickyProcessImage steps={steps} activeIndex={activeIndex} />
          </div>
        </div>

        <div className="px-5 sm:px-8">
          {steps.map((step, index) => {
            const isActive = index === activeIndex;

            return (
              <article
                key={`mobile-${step.id}`}
                id={`process-panel-mobile-${step.id}`}
                ref={(node) => {
                  mobilePanelRefs.current[index] = node;
                }}
                className="flex min-h-[70vh] w-full items-center justify-center py-8 sm:min-h-[75vh] sm:py-10"
              >
                <StepCopy
                  step={step}
                  index={index}
                  total={steps.length}
                  isActive={isActive}
                />
              </article>
            );
          })}
        </div>
      </div>

      {/* Desktop: sticky tabs + image, vertical text scroll */}
      <div className="relative mt-10 hidden lg:block">
        <div className="flex w-full flex-row px-6 xl:px-10">
          <div className="sticky top-0 z-20 flex h-screen w-[48%] shrink-0 flex-row items-stretch gap-6 bg-white/95 py-6 backdrop-blur-sm">
            <div
              className="relative flex h-full w-[7.75rem] shrink-0 items-center"
              role="tablist"
              aria-orientation="vertical"
              aria-label="Publishing process steps"
            >
              <div className="relative z-10 flex w-full flex-col justify-center gap-3">
                <div
                  className="pointer-events-none absolute left-1/2 top-[2.125rem] bottom-[2.125rem] w-px -translate-x-1/2 bg-[#b46c29]/40"
                  aria-hidden="true"
                />

                {steps.map((step, index) => {
                  const isActive = index === activeIndex;
                  const StepIcon = processIconMap[step.iconName];

                  return (
                    <button
                      key={step.id}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      aria-controls={`process-panel-${step.id}`}
                      id={`process-tab-${step.id}`}
                      tabIndex={isActive ? 0 : -1}
                      onClick={() => scrollToStep(index)}
                      onKeyDown={(event) => onTabKeyDown(event, index)}
                      className={[
                        "relative z-10 flex min-h-[4.25rem] w-full flex-col items-center justify-center rounded-lg px-2 py-2 text-center text-white shadow-sm transition-all duration-300",
                        isActive
                          ? "scale-[1.03] bg-[#29361a] ring-2 ring-[#29361a] ring-offset-2"
                          : "bg-[#b46c29] hover:bg-[#734214]",
                      ].join(" ")}
                    >
                      <StepIcon className="h-4 w-4" aria-hidden={true} />
                      <span className="mt-1.5 font-heading text-[10px] font-semibold leading-tight sm:text-[11px]">
                        {step.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="relative min-h-[min(72vh,580px)] w-full flex-1 self-stretch overflow-hidden px-1">
              <StickyProcessImage steps={steps} activeIndex={activeIndex} />
            </div>
          </div>

          <div className="w-[52%] px-8 xl:px-12">
            {steps.map((step, index) => {
              const isActive = index === activeIndex;

              return (
                <article
                  key={step.id}
                  id={`process-panel-${step.id}`}
                  role="tabpanel"
                  aria-labelledby={`process-tab-${step.id}`}
                  ref={(node) => {
                    desktopPanelRefs.current[index] = node;
                  }}
                  className="flex min-h-screen items-center py-16"
                >
                  <StepCopy
                    step={step}
                    index={index}
                    total={steps.length}
                    isActive={isActive}
                  />
                </article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
